import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllDiscounts } from '../../services/Discounts/discountsService';
import CreateDiscountModal from './CreateDiscountModal';
import EditDiscountModal from './EditDiscountModal';
import DiscountsFilter from '../Filters/DiscountsFilter';
import { FaEdit } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Discounts = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [persons, setDiscounts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreateDiscountsModal, setShowCreateDiscountsModal] = useState(false);
  const [showEditDiscountsModal, setShowEditDiscountsModal] = useState(false);
  const [selectedDiscounts, setSelectedDiscounts] = useState(null);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});

  const canView = useMemo(() => hasPermission('discountView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('discountCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('discountUpdate'), [hasPermission, permissions]);

  const fetchDiscounts = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    if (!canView) return;
    try {
      const data = await getAllDiscounts(filters, currentPage - 1, currentPageSize);
      setDiscounts(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {}
  }, [filterParams, page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchDiscounts();
    }
  }, [page, pageSize, fetchDiscounts, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreateDiscountsModal = () => {
    if (!checkPermission('discountCreate')) {
      return;
    }
    setShowCreateDiscountsModal(true);
  };
  const handleCloseCreateDiscountsModal = () => setShowCreateDiscountsModal(false);

  const handleOpenEditDiscountsModal = (discounts) => {
    if (!checkPermission('discountUpdate')) {
      return;
    }
    setSelectedDiscounts(discounts);
    setShowEditDiscountsModal(true);
  };
  
  const handleCloseEditDiscountsModal = () => {
    setSelectedDiscounts(null);
    setShowEditDiscountsModal(false);
  };
  
  const handleFilter = (filters) => {
    if (!canView) return;
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      fetchDiscounts(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  const handleClearFilter = () => {
    if (!canView) return;
    setFilterParams({});
    setPage(1);
    fetchDiscounts({}, 1, pageSize);
    previousFilterParams.current = {};
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Tabelas de Descontos</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Tabelas de Descontos</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreateDiscountsModal}>
              Cadastrar Tabela de Descontos
            </Button>
          )}
        </Col>
      </Row>
      {canView && <DiscountsFilter onFilter={handleFilter} onClear={handleClearFilter} />}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Produto</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {persons.map((discounts) => (
              <tr key={discounts.id}>
                <td>{discounts.description}</td>
                <td>{discounts.product.description}</td>
                <td style={{textAlign: 'center'}}>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditDiscountsModal(discounts)}
                      className="p-0"
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />
      
      <CreateDiscountModal
        show={showCreateDiscountsModal}
        handleClose={handleCloseCreateDiscountsModal}
        refreshDiscounts={fetchDiscounts}
      />
      <EditDiscountModal
        show={showEditDiscountsModal}
        handleClose={handleCloseEditDiscountsModal}
        discount={selectedDiscounts}
        refreshDiscounts={fetchDiscounts}
      />
    </Container>
  );
};

export default Discounts;